import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Menu,
  X,
  Calendar,
  ChevronDown,
  Building2,
  FolderOpen,
  PenTool,
  Calculator,
  Search,
  BookOpen,
  Users,
  HelpCircle,
  Sparkles,
  ArrowRight,
  Shield,
  Zap,
} from "lucide-react";
import BookDemoModal from "./modals/BookDemoModal";
import RequestDemoModal from "./modals/RequestDemoModal";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Logo from "./Logo";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isBookDemoOpen, setIsBookDemoOpen] = useState(false);
  const [isRequestDemoOpen, setIsRequestDemoOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);

  const solutionsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const resourcesTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 15);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    let attempts = 0;
    const maxAttempts = 30; // 30 intentos * 50ms = 1.5s de espera reactiva
    const pollInterval = 50;

    const tryScroll = () => {
      const element = document.getElementById(sectionId);
      if (element) {
        const headerOffset = 85;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(tryScroll, pollInterval);
      }
    };

    tryScroll();
  };

  useEffect(() => {
    if (location.hash) {
      const sectionId = location.hash.substring(1);
      scrollToSection(sectionId);
    }
  }, [location.pathname, location.hash]);

  const handleNavigation = (href: string) => {
    setSolutionsOpen(false);
    setResourcesOpen(false);
    setIsMenuOpen(false);

    if (href.startsWith("/#")) {
      const sectionId = href.substring(2);

      // Si es pricing: si estamos en home hacemos scroll suave; si estamos en otra página navegamos a /pricing
      if (sectionId === "pricing") {
        if (location.pathname === "/") {
          const localElement = document.getElementById("pricing");
          if (localElement) {
            localElement.scrollIntoView({ behavior: "smooth", block: "start" });
            return;
          }
        }
        navigate("/pricing");
        return;
      }

      if (location.pathname !== "/") {
        navigate(href);
      } else {
        scrollToSection(sectionId);
      }
      return;
    }

    navigate(href);
  };

  const solutionsList = [
    {
      title: "Cartera PH (Ley 675)",
      desc: "Cobranza masiva con botón PSE por WhatsApp y Título Ejecutivo.",
      href: "/cartera-ph",
      icon: Building2,
      badge: "Especializado",
    },
    {
      title: "Control de Expedientes",
      desc: "Monitoreo de autos, traslados y términos en la Rama Judicial.",
      href: "/control-expedientes",
      icon: FolderOpen,
      badge: "Tyba / Samai",
    },
    {
      title: "Generador de Documentos",
      desc: "Redacción de tutelas, demandas, contestaciones y contratos.",
      href: "/features/document-drafting",
      icon: PenTool,
      badge: "IA Legal",
    },
    {
      title: "Calculadoras Judiciales & CSJ",
      desc: "Perjuicios traslado pensional CSJ, laboral Art. 64 CST e indexación.",
      href: "/calculadoras",
      icon: Calculator,
      badge: "Oficial CSJ",
    },
    {
      title: "Investigación en 1.8M",
      desc: "Búsqueda semántica en sentencias de Altas Cortes colombianas.",
      href: "/features/legal-research",
      icon: Search,
      badge: "Cero Alucinaciones",
    },
  ];

  const resourcesList = [
    {
      title: "Calculadora de Ahorro",
      desc: "Calcula el retorno de inversión y tiempo ahorrado en tu firma.",
      href: "/calculadora",
      icon: Zap,
    },
    {
      title: "Blog Jurídico",
      desc: "Análisis doctrinarios, jurisprudencia reciente e IA legal.",
      href: "/blog",
      icon: BookOpen,
    },
    {
      title: "Sobre Nosotros",
      desc: "Nuestra misión democratizando el derecho en Colombia.",
      href: "/about-us",
      icon: Users,
    },
    {
      title: "Preguntas Frecuentes",
      desc: "Dudas sobre seguridad Habeas Data, RAG y soporte.",
      href: "/#faq",
      icon: HelpCircle,
    },
  ];

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/90 dark:bg-slate-950/90 backdrop-blur-xl shadow-md shadow-slate-900/5 border-b border-slate-200/80 dark:border-slate-800"
          : "bg-white/70 dark:bg-slate-950/70 backdrop-blur-md border-b border-slate-200/50 dark:border-slate-800/60"
      }`}
    >
      <div className="container mx-auto flex items-center justify-between h-20 px-4 md:px-6">
        {/* Brand Logo */}
        <Link to="/" className="flex items-center shrink-0">
          <Logo />
        </Link>

        {/* Desktop Navigation Links */}
        <nav className="hidden lg:flex items-center gap-1 xl:gap-2">
          {/* Soluciones Dropdown */}
          <div
            className="relative"
            onMouseEnter={() => {
              if (solutionsTimeoutRef.current) clearTimeout(solutionsTimeoutRef.current);
              setSolutionsOpen(true);
            }}
            onMouseLeave={() => {
              solutionsTimeoutRef.current = setTimeout(() => setSolutionsOpen(false), 150);
            }}
          >
            <button
              onClick={() => handleNavigation("/#features")}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-semibold transition-colors ${
                solutionsOpen
                  ? "bg-slate-100 dark:bg-slate-800 text-blue-600 dark:text-blue-400"
                  : "text-slate-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-900"
              }`}
            >
              <span>Soluciones</span>
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-200 ${
                  solutionsOpen ? "rotate-180 text-blue-600" : "text-slate-400"
                }`}
              />
            </button>

            {/* Dropdown Menu Panel */}
            {solutionsOpen && (
              <div className="absolute top-full left-0 w-[420px] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200/80 dark:border-slate-800 p-3 mt-1 animate-in fade-in slide-in-from-top-2 duration-200 z-50">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 px-3 py-1.5">
                  Módulos de la Plataforma
                </div>
                <div className="space-y-1">
                  {solutionsList.map((item, idx) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={idx}
                        onClick={() => handleNavigation(item.href)}
                        className="w-full text-left p-2.5 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/70 transition-colors flex items-start gap-3 group"
                      >
                        <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-950/70 flex items-center justify-center text-blue-600 dark:text-blue-400 shrink-0 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-grow">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-bold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                              {item.title}
                            </span>
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                              {item.badge}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-snug">
                            {item.desc}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Direct Link: Precios */}
          <button
            onClick={() => handleNavigation("/#pricing")}
            className="px-3.5 py-2 rounded-xl text-sm font-semibold text-slate-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors"
          >
            Precios
          </button>

          {/* Recursos Dropdown */}
          <div
            className="relative"
            onMouseEnter={() => {
              if (resourcesTimeoutRef.current) clearTimeout(resourcesTimeoutRef.current);
              setResourcesOpen(true);
            }}
            onMouseLeave={() => {
              resourcesTimeoutRef.current = setTimeout(() => setResourcesOpen(false), 150);
            }}
          >
            <button
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-semibold transition-colors ${
                resourcesOpen
                  ? "bg-slate-100 dark:bg-slate-800 text-blue-600 dark:text-blue-400"
                  : "text-slate-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-900"
              }`}
            >
              <span>Recursos</span>
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-200 ${
                  resourcesOpen ? "rotate-180 text-blue-600" : "text-slate-400"
                }`}
              />
            </button>

            {/* Dropdown Menu Panel */}
            {resourcesOpen && (
              <div className="absolute top-full left-0 w-[320px] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200/80 dark:border-slate-800 p-3 mt-1 animate-in fade-in slide-in-from-top-2 duration-200 z-50">
                <div className="space-y-1">
                  {resourcesList.map((item, idx) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={idx}
                        onClick={() => handleNavigation(item.href)}
                        className="w-full text-left p-2.5 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/70 transition-colors flex items-start gap-3 group"
                      >
                        <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-600 dark:text-slate-400 shrink-0 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                          <Icon className="w-4 h-4" />
                        </div>
                        <div>
                          <span className="text-sm font-bold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors block">
                            {item.title}
                          </span>
                          <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-snug">
                            {item.desc}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </nav>

        {/* Action Buttons (Right side) */}
        <div className="hidden lg:flex items-center gap-3">
          <Button
            variant="ghost"
            className="text-xs sm:text-sm font-bold text-slate-700 dark:text-slate-300 hover:text-blue-600 hover:bg-slate-100 dark:hover:bg-slate-800 h-10 px-3.5 rounded-xl"
            onClick={() => setIsRequestDemoOpen(true)}
          >
            <Calendar className="w-4 h-4 mr-1.5 text-blue-600" />
            <span>Solicitar Demo</span>
          </Button>

          <Button
            variant="outline"
            className="text-xs sm:text-sm font-bold border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-slate-800 h-10 px-4 rounded-xl"
            onClick={() => navigate("/login")}
          >
            Iniciar Sesión
          </Button>

          <Button
            className="bg-blue-600 hover:bg-blue-700 text-white font-extrabold text-xs sm:text-sm h-10 px-5 rounded-xl shadow-md shadow-blue-600/20 hover:shadow-lg hover:scale-105 transition-all flex items-center gap-1.5"
            onClick={() => navigate("/signup")}
          >
            <span>Comenzar Gratis</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Button>
        </div>

        {/* Mobile Hamburger Toggle */}
        <div className="lg:hidden flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="text-slate-900 dark:text-white rounded-xl h-10 w-10"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </Button>
        </div>
      </div>

      {/* Mobile Drawer Navigation */}
      {isMenuOpen && (
        <div className="lg:hidden bg-white dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 px-4 py-6 shadow-2xl animate-in slide-in-from-top-4 duration-300">
          <div className="space-y-6">
            {/* Soluciones Section */}
            <div>
              <span className="text-[11px] font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400 block mb-3">
                Soluciones Jurídicas
              </span>
              <div className="space-y-1">
                {solutionsList.map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={idx}
                      onClick={() => handleNavigation(item.href)}
                      className="w-full text-left p-2.5 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-900 flex items-center justify-between text-sm font-bold text-slate-900 dark:text-white"
                    >
                      <div className="flex items-center gap-2.5">
                        <Icon className="w-4 h-4 text-blue-600" />
                        <span>{item.title}</span>
                      </div>
                      <span className="text-[10px] text-slate-400">{item.badge}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* General Links */}
            <div className="pt-2 border-t border-slate-100 dark:border-slate-800 space-y-1">
              <button
                onClick={() => handleNavigation("/#pricing")}
                className="w-full text-left p-2.5 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-900 text-sm font-bold text-slate-900 dark:text-white block"
              >
                Precios & Planes
              </button>
              <button
                onClick={() => handleNavigation("/calculadora")}
                className="w-full text-left p-2.5 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-900 text-sm font-bold text-slate-900 dark:text-white block"
              >
                Calculadora de Ahorro
              </button>
              <button
                onClick={() => handleNavigation("/blog")}
                className="w-full text-left p-2.5 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-900 text-sm font-bold text-slate-900 dark:text-white block"
              >
                Blog Jurídico
              </button>
              <button
                onClick={() => handleNavigation("/#faq")}
                className="w-full text-left p-2.5 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-900 text-sm font-bold text-slate-900 dark:text-white block"
              >
                Preguntas Frecuentes
              </button>
            </div>

            {/* Mobile Actions */}
            <div className="pt-4 border-t border-slate-100 dark:border-slate-800 space-y-2.5">
              <Button
                variant="outline"
                className="w-full h-11 rounded-xl text-sm font-bold border-slate-300 dark:border-slate-700"
                onClick={() => {
                  setIsMenuOpen(false);
                  setIsRequestDemoOpen(true);
                }}
              >
                <Calendar className="w-4 h-4 mr-2 text-blue-600" />
                Solicitar Demostración
              </Button>
              <Button
                variant="outline"
                className="w-full h-11 rounded-xl text-sm font-bold border-blue-600 text-blue-600 hover:bg-blue-50"
                onClick={() => {
                  setIsMenuOpen(false);
                  navigate("/login");
                }}
              >
                Iniciar Sesión
              </Button>
              <Button
                className="w-full h-11 rounded-xl text-sm font-extrabold bg-blue-600 hover:bg-blue-700 text-white shadow-md shadow-blue-600/20"
                onClick={() => {
                  setIsMenuOpen(false);
                  navigate("/signup");
                }}
              >
                Comenzar Gratis
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <BookDemoModal isOpen={isBookDemoOpen} onClose={() => setIsBookDemoOpen(false)} />
      <RequestDemoModal isOpen={isRequestDemoOpen} onClose={() => setIsRequestDemoOpen(false)} />
    </header>
  );
};

export default Header;
